import React, { useState } from 'react';
import { toast } from 'sonner';
import { ActionDialog, Field, inputCls, primaryBtn } from './ActionDialog';
import { buildHHCCN, buildNOMNC, patientName } from '../../utils/admissionDocs';
import { savePdfToPackages } from '../../utils/documentExport';
import { AGENCY } from '../../utils/agencyInfo';
import { SignaturePad } from './SignaturePad';

const KINDS = {
  hhccn: { title: 'Home Health Change of Care Notice (HHCCN)', build: buildHHCCN, file: 'HHCCN' },
  nomnc: { title: 'Notice of Medicare Non-Coverage (NOMNC)', build: buildNOMNC, file: 'NOMNC' },
};

export const NoticeDialog = ({ kind, patient, onClose }) => {
  const k = KINDS[kind];
  const [v, setV] = useState({
    patientName: patientName(patient), idNumber: patient.medicareNumber || patient.insuranceMemberId || patient.pan || '',
    effectiveDate: '', change: 'Reduced', reason: 'physician', items: '', explanation: '', agencyPhone: AGENCY.phone,
    qioName: 'Livanta BFCC-QIO', qioPhone: '1-877-588-1123', qioTty: '1-855-887-6668', additional: '', signature: '', signerName: patientName(patient), signerRelation: 'Patient',
  });
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setV({ ...v, [key]: e.target.value });
  const create = async () => {
    if (!v.effectiveDate) { toast.error('Enter the effective date'); return; }
    setBusy(true);
    try {
      const doc = k.build(v);
      const signed = Boolean(v.signature);
      const filename = `${k.file}_${v.patientName.replace(/\s+/g, '_')}_${v.effectiveDate}${signed ? '_signed' : ''}.pdf`;
      doc.save(filename);
      const note = signed ? `signed by ${v.signerName || v.patientName} (${v.signerRelation}) on ${new Date().toLocaleDateString('en-US')}` : 'unsigned';
      await savePdfToPackages(patient.id, doc.output('blob'), filename, `${k.file} effective ${v.effectiveDate} - ${note}`);
      toast.success(`${k.file} ${signed ? 'signed, ' : ''}downloaded and saved to the chart`);
      onClose();
    } catch (e) { toast.error(`Downloaded, but could not save to chart: ${e.message}`); }
    finally { setBusy(false); }
  };
  return (
    <ActionDialog title={k.title} onClose={onClose} testid={`${kind}-dialog`}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Patient name"><input className={inputCls} value={v.patientName} onChange={set('patientName')} /></Field>
          <Field label="ID / Medicare number"><input className={inputCls} value={v.idNumber} onChange={set('idNumber')} /></Field>
        </div>
        <Field label={kind === 'nomnc' ? 'Date coverage ends' : 'Effective date of change'}><input type="date" className={inputCls} value={v.effectiveDate} onChange={set('effectiveDate')} data-testid={`${kind}-effective-date`} /></Field>
        {kind === 'hhccn' ? <HhccnFields v={v} set={set} /> : <NomncFields v={v} set={set} />}
        <div className="border-t pt-3 space-y-2">
          <p className="text-sm font-semibold text-[#1e40af]">Patient signature (optional — hand the tablet to the patient)</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Signed by"><input className={inputCls} value={v.signerName} onChange={set('signerName')} data-testid={`${kind}-signer-name`} /></Field>
            <Field label="Relationship"><select className={inputCls} value={v.signerRelation} onChange={set('signerRelation')} data-testid={`${kind}-signer-relation`}>
              {['Patient', 'Representative', 'Spouse', 'Son/Daughter', 'Caregiver', 'Legal guardian / POA'].map(o => <option key={o}>{o}</option>)}
            </select></Field>
          </div>
          <SignaturePad onChange={(sig) => setV(prev => ({ ...prev, signature: sig }))} testid={`${kind}-signature-pad`} />
        </div>
        <p className="text-xs text-gray-500">Creates an editable PDF for signature and saves a copy in the patient&apos;s Application Packages.</p>
        <div className="flex justify-end"><button className={primaryBtn} onClick={create} disabled={busy} data-testid={`${kind}-create`}>{busy ? 'Creating…' : v.signature ? 'Create signed notice' : 'Create notice'}</button></div>
      </div>
    </ActionDialog>
  );
};

const HhccnFields = ({ v, set }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Services are being"><select className={inputCls} value={v.change} onChange={set('change')}><option>Reduced</option><option>Stopped</option></select></Field>
      <Field label="Reason"><select className={inputCls} value={v.reason} onChange={set('reason')}><option value="physician">Physician changed/cancelled order</option><option value="agency">Agency decision</option></select></Field>
    </div>
    <Field label="Items / services changing"><textarea rows={3} className={inputCls} value={v.items} onChange={set('items')} data-testid="hhccn-items" /></Field>
    <Field label="Explanation"><textarea rows={2} className={inputCls} value={v.explanation} onChange={set('explanation')} /></Field>
  </>
);

const NomncFields = ({ v, set }) => (
  <>
    <div className="grid grid-cols-3 gap-3">
      <Field label="QIO name"><input className={inputCls} value={v.qioName} onChange={set('qioName')} /></Field>
      <Field label="QIO phone"><input className={inputCls} value={v.qioPhone} onChange={set('qioPhone')} /></Field>
      <Field label="QIO TTY"><input className={inputCls} value={v.qioTty} onChange={set('qioTty')} /></Field>
    </div>
    <Field label="Additional information (optional)"><textarea rows={2} className={inputCls} value={v.additional} onChange={set('additional')} /></Field>
  </>
);
